/**
 * Rechte und Rollen – die eine Stelle, an der steht, wer was darf.
 *
 * Warum überhaupt: Bis hierher stand jede Prüfung von Hand in der jeweiligen
 * Route (`if (!canBroadcast(user.role)) …`). Bei 242 ändernden Routen hieß das:
 * 16 Dateien prüften gar nichts, und niemand merkte es. Rechte gehören darum in
 * einen Katalog, Routen nennen nur noch das benötigte Recht.
 *
 * Zwei Ebenen, die zusammenwirken:
 *
 *   1. Die Rolle im Radio (`users.role`) – gilt überall im Mandanten.
 *   2. Die Rolle im Kanal (`channel_members.role`) – gilt nur dort.
 *
 * Das wirksame Recht für eine Handlung an Kanal X ist die **Vereinigung** aus
 * beidem: Wer im Radio Bibliothek ist, aber auf Kanal 2 als Moderation
 * eingetragen, darf auf Kanal 2 moderieren. Umgekehrt entzieht die Kanalrolle
 * nichts, was die Rolle im Radio schon erlaubt – sonst wäre nicht mehr
 * nachvollziehbar, woher ein „verboten“ kommt.
 */
// UserRole bleibt in types/ definiert - hier nur benutzt, nicht neu erklaert,
// sonst kollidiert der Stern-Export in index.ts.
export const USER_ROLES = [
    "super_admin",
    "tenant_admin",
    "programmleitung",
    "moderator",
    "redaktion",
    "librarian",
    "technik",
    "trainee",
];
/** Klarnamen fuer Oberflaeche und Doku – die Datenbank speichert die Schluessel. */
export const ROLE_LABELS = {
    super_admin: "Betreiber",
    tenant_admin: "Radioleitung",
    programmleitung: "Programmleitung",
    moderator: "Moderation",
    redaktion: "Redaktion",
    librarian: "Musikredaktion",
    technik: "Technik",
    trainee: "Anwärter",
};
/** Ein Satz je Rolle – was diese Person im Alltag tut. */
export const ROLE_DESCRIPTIONS = {
    super_admin: "Betreibt die Installation: Radios anlegen, Icecast/SHOUTcast der Suite, Studio-Pakete.",
    tenant_admin: "Leitet das Radio. Als Einzige vergeben Radioleitungen Zugänge, Rollen und Kanäle.",
    programmleitung: "Plant das Programm: Sendeplan, Stundenuhren, Sendungen, Moderatoren, Werbeplätze.",
    moderator: "Geht auf Sendung, betreut Wünsche, Grüße, Telefon und Abstimmungen.",
    redaktion: "Schreibt Beiträge und pflegt die Nachrichten. Freigeben darf die Redaktionsleitung.",
    librarian: "Pflegt Mediathek, Playlists und Rotation – ohne in den laufenden Betrieb einzugreifen.",
    technik: "Kanäle, Ausgänge, Streaming-Server und Zugangsdaten. Kein Zugriff auf Inhalte.",
    trainee: "Anwärter: übt auf dem Übungskanal, sonst nichts.",
};
export const isUserRole = (value) => typeof value === "string" && USER_ROLES.includes(value);
/**
 * Der Rechtekatalog. Namensschema `gebiet.handlung`.
 *
 * `read` steht nur dort, wo Lesen wirklich schützenswert ist (Zugangsdaten,
 * Auswertung, Mitschnitte). Alles andere darf jede angemeldete Person des
 * Radios lesen – das war bisher so und soll sich nicht still ändern.
 */
export const PERMISSIONS = [
    // Mediathek und Musik
    "library.write", // Titel hochladen, ändern, löschen, neu messen
    "playlists.write",
    "jingles.write",
    // Sendebetrieb
    "playout.control", // Nächster Titel, Jingle drüberlegen, Warteschlange
    "schedule.write", // Sendeplan, Stundenuhren, Blöcke, Werbeplätze
    "live.credentials.read", // Live-Zugangsdaten und SRT einsehen
    "channel.manage", // Kanäle, Ausgänge, Server anlegen und ändern
    "channel.members.manage", // Wer darf auf welchem Kanal mitarbeiten
    // Programm und Menschen
    "shows.write", // Sendungen, Moderatoren
    "news.write", // Nachrichtenblöcke, Quellen, Auffrischen
    // Redaktion (das Recht `editorial_lead` kommt zusätzlich obendrauf)
    "posts.write", // Beiträge schreiben und bearbeiten
    "posts.publish", // freigeben, planen, zurückziehen
    // Hörerbindung
    "interaction.moderate", // Wünsche, Grüße, Umfragen, Quiz, Gewinnspiele, Telefon
    "requests.moderate",
    // Auswertung und Nachweise
    "stats.read",
    "reports.read", // GEMA/GVL, Werbenachweis
    "recordings.read", // Mitschnitte aus dem Probebetrieb (personenbezogen!)
    // Rund ums Radio
    "site.write", // Hörerseite, Branding, Seiten, Podcast
    "events.write", // Veranstaltungen, Kalender
    "users.manage", // Zugänge und Rollen im eigenen Radio
    "tenant.manage", // Radio-Einstellungen, Meldeziele, Module
    "practice.use", // Probebetrieb – das Einzige, was Anwärter dürfen
    // Nur Betreiber der ganzen Installation
    "platform.manage", // Radios anlegen, Icecast/SHOUTcast der Suite, Studio-Pakete
];
const alle = (...p) => new Set(p);
/**
 * Rolle → Rechte. Bewusst ausgeschrieben statt vererbt: Man soll in einer
 * Zeile sehen können, was eine Rolle darf, ohne eine Kette zu verfolgen.
 */
const MATRIX = {
    // Betreiber der Installation: alles, plus die Plattform selbst.
    super_admin: new Set(PERMISSIONS),
    // Alles im eigenen Radio – aber nicht die Plattform.
    tenant_admin: new Set(PERMISSIONS.filter((p) => p !== "platform.manage")),
    moderator: alle("library.write", "playlists.write", "jingles.write", "playout.control", "schedule.write", "live.credentials.read", "shows.write", "news.write", "posts.write", "interaction.moderate", "requests.moderate", "stats.read", "events.write", "practice.use"),
    librarian: alle("library.write", "playlists.write", "jingles.write", "stats.read", "reports.read", "practice.use"),
    // Programm und Sendeplan - aber KEINE Zugaenge und keine Kanalmitglieder:
    // wer darf was, bleibt bei der Radioleitung.
    programmleitung: alle("library.write", "playlists.write", "jingles.write", "playout.control", "schedule.write", "live.credentials.read", "shows.write", "news.write", "posts.write", "interaction.moderate", "requests.moderate", "stats.read", "reports.read", "events.write", "site.write", "practice.use"),
    // Wort statt Musik: Beitraege und Nachrichten. Freigeben haengt weiterhin
    // am Recht `editorial_lead`, nicht an der Rolle.
    redaktion: alle("posts.write", "news.write", "events.write", "site.write", "stats.read", "practice.use"),
    // Technik fasst Inhalte nicht an, dafuer alles rund um Ausspielung.
    technik: alle("channel.manage", "live.credentials.read", "playout.control", "stats.read", "practice.use"),
    // Anwärter: ausschliesslich Probebetrieb. Siehe docs/SECURITY.md.
    trainee: alle("practice.use"),
};
/** Rechte einer Rolle – leere Menge bei unbekannter Rolle (bewusst: im Zweifel nichts). */
export function permissionsOf(role) {
    return isUserRole(role) ? MATRIX[role] : new Set();
}
/**
 * Wirksame Rechte: Rolle im Radio vereinigt mit der Rolle im betroffenen Kanal.
 * `channelRole` ist undefined, wenn die Handlung keinen Kanal betrifft oder die
 * Person dort kein Mitglied ist.
 */
export function effectivePermissions(role, channelRole) {
    const eigene = permissionsOf(role);
    if (!channelRole)
        return eigene;
    const vereint = new Set(eigene);
    for (const p of permissionsOf(channelRole))
        vereint.add(p);
    return vereint;
}
export function can(role, permission, channelRole) {
    return effectivePermissions(role, channelRole).has(permission);
}
/**
 * Rechte, die sich immer auf einen bestimmten Kanal beziehen. Bei ihnen genügt
 * die Rolle im Radio nicht – es braucht zusätzlich Zugang zu genau diesem Kanal
 * (oder Admin-Rang, siehe `seesAllChannels`).
 */
export const CHANNEL_SCOPED = new Set([
    "playout.control",
    "schedule.write",
    "live.credentials.read",
    "jingles.write",
    "channel.manage",
    "channel.members.manage",
]);
/** Admins sehen und bedienen alle Kanäle ihres Radios, ohne Mitgliedschaft. */
export function seesAllChannels(role) {
    return role === "super_admin" || role === "tenant_admin";
}
